import { ARSText } from "../inputs/ARSText";

/**
 * Props for the NamedObjectDisplay component.
 */
export interface NamedObjectDisplayProps {
  /** RFC 6901 JSON Pointer path to the NamedObject in ARSContext state */
  dataPath: string;
  /** Can rename the object */
  canRename?: boolean;
}

/**
 * A component for displaying and editing NamedObject properties (name, description, label).
 *
 * This component provides inputs for the three standard fields that are shared across many
 * ARS entities (AnalysisSet, DataSubset, Group, etc.):
 * - name: The name for the instance (required)
 * - description: A textual description (optional)
 * - label: A short informative description for display (optional)
 *
 * All changes are synchronised with ARSContext state using the PATCH_ARS_STATE action
 * through the ARSText components.
 *
 * @param props - Component props.
 * @param props.dataPath - RFC 6901 JSON Pointer path to the NamedObject in ARSContext state.
 * @param props.canRename - Optional flag to allow renaming the object.
 * @returns A React functional component rendering the named object editor.
 */
export const NamedObjectDisplay = ({
  dataPath,
  canRename = false,
}: NamedObjectDisplayProps): React.ReactElement => {
  return (
    <div>
      <ARSText
        dataPath={`${dataPath}/name`}
        id={`${dataPath.replace(/\//g, "-")}-name`}
        label="Name"
        labelWidth={2}
        placeholder="Enter name"
        disabled={!canRename}
      />
      <ARSText
        dataPath={`${dataPath}/label`}
        id={`${dataPath.replace(/\//g, "-")}-label`}
        label="Label"
        labelWidth={2}
        placeholder="Enter label"
      />
      <ARSText
        dataPath={`${dataPath}/description`}
        id={`${dataPath.replace(/\//g, "-")}-description`}
        label="Description"
        labelWidth={2}
        type="textarea"
        rows={3}
        placeholder="Enter description"
        allowCarriageReturn={true}
      />
    </div>
  );
};

NamedObjectDisplay.displayName = "NamedObjectDisplay";
